export type SchemaVariantAttribute =
  | 'color'
  | 'size'
  | 'material'
  | 'pattern'
  | 'suggestedAge';

export const SCHEMA_VARIANT_ATTRIBUTE_URIS: Record<
  SchemaVariantAttribute,
  string
> = {
  color: 'https://schema.org/color',
  size: 'https://schema.org/size',
  material: 'https://schema.org/material',
  pattern: 'https://schema.org/pattern',
  suggestedAge: 'https://schema.org/suggestedAge',
};

export type VariantAttributeTokenGroup = Readonly<{
  tokens: ReadonlySet<string>;
}>;

export type VariantAttributeSizeGroup = Readonly<{
  tokens: ReadonlySet<string>;
  denyTokens: ReadonlySet<string>;
}>;

export type VariantAttributeSuggestedAgeGroup = Readonly<{
  tokens: ReadonlySet<string>;
  phrases: ReadonlyArray<readonly string[]>;
}>;

export type VariantAttributeDictionary = Readonly<{
  color: VariantAttributeTokenGroup;
  size: VariantAttributeSizeGroup;
  material: VariantAttributeTokenGroup;
  pattern: VariantAttributeTokenGroup;
  suggestedAge: VariantAttributeSuggestedAgeGroup;
}>;

export type SanityVariantAttributeTokenGroup = Readonly<{
  tokens?: ReadonlyArray<string | null> | null;
  denyTokens?: ReadonlyArray<string | null> | null;
  phrases?: ReadonlyArray<string | null> | null;
}>;

export type SanityVariantAttributeDictionaryInput = Readonly<{
  color?: SanityVariantAttributeTokenGroup | null;
  size?: SanityVariantAttributeTokenGroup | null;
  material?: SanityVariantAttributeTokenGroup | null;
  pattern?: SanityVariantAttributeTokenGroup | null;
  suggestedAge?: SanityVariantAttributeTokenGroup | null;
}> | null;

const COMBINING_MARKS = /\p{M}/gu;

export const DEFAULT_VARIANT_ATTRIBUTE_DICTIONARY: VariantAttributeDictionary =
  {
    color: {
      tokens: new Set([
        'color',
        'colour',
        'farbe',
        'farb',
        'couleur',
        'colore',
        'kleur',
      ]),
    },
    size: {
      tokens: new Set([
        'size',
        'dimension',
        'dimensions',
        'taille',
        'grosse',
        'groesse',
        'taglia',
        'misura',
        'talla',
        'tamano',
      ]),
      denyTokens: new Set([
        'pack',
        'packs',
        'packet',
        'quantity',
        'qty',
        'count',
        'counts',
        'bundle',
        'bundles',
        'multipack',
        'paquet',
        'lot',
        'quantite',
        'packung',
        'menge',
        'confezione',
        'pacco',
        'paquete',
        'cantidad',
      ]),
    },
    material: {
      tokens: new Set([
        'material',
        'fabric',
        'matiere',
        'tissu',
        'stoff',
        'gewebe',
        'materiale',
        'tessuto',
        'tejido',
      ]),
    },
    pattern: {
      tokens: new Set([
        'pattern',
        'design',
        'motif',
        'muster',
        'motivo',
        'estampado',
        'patron',
        'diseno',
      ]),
    },
    suggestedAge: {
      tokens: new Set(['age', 'alter', 'eta', 'edad', 'lebensphase']),
      phrases: [
        ['life', 'stage'],
        ['pet', 'age'],
        ['stade', 'de', 'vie'],
        ['fase', 'della', 'vita'],
        ['fase', 'di', 'vita'],
        ['etapa', 'de', 'vida'],
      ],
    },
  };

const tokenizeOptionName = (optionName: string): string[] => {
  const normalized = optionName
    .toLowerCase()
    .trim()
    .replaceAll('ß', 'ss')
    .normalize('NFD')
    .replace(COMBINING_MARKS, '')
    .replaceAll('_', ' ')
    .replaceAll('-', ' ')
    .replaceAll("'", ' ')
    .replaceAll('’', ' ')
    .replaceAll('ʼ', ' ');

  const tokens: string[] = [];
  for (const part of normalized.split(' ')) {
    if (part) tokens.push(part);
  }
  return tokens;
};

export const isSingleVariantAttributeToken = (value: string): boolean =>
  tokenizeOptionName(value).length === 1;

const toTokenSet = (
  values: ReadonlyArray<string | null | undefined> | null | undefined
): Set<string> => {
  const tokens = new Set<string>();
  if (!values) return tokens;

  for (const value of values) {
    if (!value) continue;
    for (const token of tokenizeOptionName(value)) {
      tokens.add(token);
    }
  }
  return tokens;
};

const toPhraseList = (
  values: ReadonlyArray<string | null | undefined> | null | undefined
): readonly string[][] => {
  if (!values) return [];

  const phrases: string[][] = [];
  for (const value of values) {
    if (!value) continue;
    const tokens = tokenizeOptionName(value);
    if (tokens.length === 0) continue;
    phrases.push(tokens);
  }
  return phrases;
};

const setOrDefault = (
  tokens: ReadonlySet<string>,
  fallback: ReadonlySet<string>
): ReadonlySet<string> => {
  if (tokens.size === 0) return fallback;
  return tokens;
};

const phrasesOrDefault = (
  phrases: ReadonlyArray<readonly string[]>,
  fallback: ReadonlyArray<readonly string[]>
): ReadonlyArray<readonly string[]> => {
  if (phrases.length === 0) return fallback;
  return phrases;
};

const dictionaryHasContent = (
  dictionary: VariantAttributeDictionary
): boolean =>
  dictionary.color.tokens.size > 0 ||
  dictionary.size.tokens.size > 0 ||
  dictionary.size.denyTokens.size > 0 ||
  dictionary.material.tokens.size > 0 ||
  dictionary.pattern.tokens.size > 0 ||
  dictionary.suggestedAge.tokens.size > 0 ||
  dictionary.suggestedAge.phrases.length > 0;

const fillEmptyDictionaryGroups = (
  dictionary: VariantAttributeDictionary
): VariantAttributeDictionary => {
  const defaults = DEFAULT_VARIANT_ATTRIBUTE_DICTIONARY;

  return {
    color: {
      tokens: setOrDefault(dictionary.color.tokens, defaults.color.tokens),
    },
    size: {
      tokens: setOrDefault(dictionary.size.tokens, defaults.size.tokens),
      denyTokens: setOrDefault(
        dictionary.size.denyTokens,
        defaults.size.denyTokens
      ),
    },
    material: {
      tokens: setOrDefault(
        dictionary.material.tokens,
        defaults.material.tokens
      ),
    },
    pattern: {
      tokens: setOrDefault(dictionary.pattern.tokens, defaults.pattern.tokens),
    },
    suggestedAge: {
      tokens: setOrDefault(
        dictionary.suggestedAge.tokens,
        defaults.suggestedAge.tokens
      ),
      phrases: phrasesOrDefault(
        dictionary.suggestedAge.phrases,
        defaults.suggestedAge.phrases
      ),
    },
  };
};

export const mapSanityVariantAttributeDictionary = (
  input: SanityVariantAttributeDictionaryInput | undefined
): VariantAttributeDictionary | undefined => {
  if (!input) return undefined;

  const dictionary: VariantAttributeDictionary = {
    color: { tokens: toTokenSet(input.color?.tokens) },
    size: {
      tokens: toTokenSet(input.size?.tokens),
      denyTokens: toTokenSet(input.size?.denyTokens),
    },
    material: { tokens: toTokenSet(input.material?.tokens) },
    pattern: { tokens: toTokenSet(input.pattern?.tokens) },
    suggestedAge: {
      tokens: toTokenSet(input.suggestedAge?.tokens),
      phrases: toPhraseList(input.suggestedAge?.phrases),
    },
  };

  if (!dictionaryHasContent(dictionary)) return undefined;
  return fillEmptyDictionaryGroups(dictionary);
};

const hasAnyToken = (
  tokens: readonly string[],
  allowed: ReadonlySet<string>
): boolean => {
  for (const token of tokens) {
    if (allowed.has(token)) return true;
  }
  return false;
};

const hasConsecutivePhrase = (
  tokens: readonly string[],
  phraseTokens: readonly string[]
): boolean => {
  if (phraseTokens.length > tokens.length) return false;

  const lastStart = tokens.length - phraseTokens.length;
  for (let start = 0; start <= lastStart; start++) {
    if (matchesPhraseAt(tokens, phraseTokens, start)) return true;
  }
  return false;
};

const matchesPhraseAt = (
  tokens: readonly string[],
  phraseTokens: readonly string[],
  start: number
): boolean => {
  for (let index = 0; index < phraseTokens.length; index++) {
    if (tokens[start + index] !== phraseTokens[index]) return false;
  }
  return true;
};

const hasSuggestedAgePhrase = (
  tokens: readonly string[],
  phrases: ReadonlyArray<readonly string[]>
): boolean => {
  for (const phraseTokens of phrases) {
    if (hasConsecutivePhrase(tokens, phraseTokens)) return true;
  }
  return false;
};

const resolveSizeAttribute = (
  tokens: readonly string[],
  sizeGroup: VariantAttributeSizeGroup
): 'size' | undefined => {
  if (!hasAnyToken(tokens, sizeGroup.tokens)) return undefined;
  if (hasAnyToken(tokens, sizeGroup.denyTokens)) return undefined;
  return 'size';
};

export const resolveSchemaVariantAttribute = (
  optionName: string | undefined,
  dictionary: VariantAttributeDictionary = DEFAULT_VARIANT_ATTRIBUTE_DICTIONARY
): SchemaVariantAttribute | undefined => {
  if (!optionName) return undefined;

  const tokens = tokenizeOptionName(optionName);
  if (tokens.length === 0) return undefined;

  if (hasSuggestedAgePhrase(tokens, dictionary.suggestedAge.phrases)) {
    return 'suggestedAge';
  }

  const sizeAttribute = resolveSizeAttribute(tokens, dictionary.size);
  if (sizeAttribute) return sizeAttribute;

  if (hasAnyToken(tokens, dictionary.color.tokens)) return 'color';
  if (hasAnyToken(tokens, dictionary.material.tokens)) return 'material';
  if (hasAnyToken(tokens, dictionary.pattern.tokens)) return 'pattern';
  if (hasAnyToken(tokens, dictionary.suggestedAge.tokens)) return 'suggestedAge';

  return undefined;
};

export const getSchemaVariantAttributeUrl = (
  optionName: string | undefined,
  dictionary: VariantAttributeDictionary = DEFAULT_VARIANT_ATTRIBUTE_DICTIONARY
): string | undefined => {
  const attribute = resolveSchemaVariantAttribute(optionName, dictionary);
  if (!attribute) return undefined;
  return SCHEMA_VARIANT_ATTRIBUTE_URIS[attribute];
};
